import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import httpService from "../services/httpService";
import posthog from 'posthog-js';
import { useAuthStore } from "../store/store";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import AnimatedImage from "../components/AnimatedImage";

const Join = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token } = useAuthStore();

    const [code, setCode] = useState(searchParams.get("code") || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clubPreview, setClubPreview] = useState(null); // shown after unauthenticated redeem

    // Auto-submit if code came from query param
    useEffect(() => {
        const paramCode = searchParams.get("code");
        if (paramCode) handleRedeem(null, paramCode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRedeem = async (e, overrideCode) => {
        if (e) e.preventDefault();
        const codeToSend = (overrideCode ?? code).trim();
        if (!codeToSend) return;

        setLoading(true);
        setError(null);
        setClubPreview(null);

        try {
            const res = await httpService.post("invite/redeem", { code: codeToSend });
            const data = res.data.data;

            posthog.capture('invite_code_redeemed', {
                deferred: !!data?.deferred,
                requires_signup: !!data?.signup_token,
                club_guid: data?.podcast_club?.guid ?? null,
            });

            if (data?.deferred) {
                // Logged-in user with incomplete profile — auto-register as listener so the server
                // redeems the stored signup_token and subscribes them to the club
                await httpService.post("dash/register-demographics", { role: 'listener' });
                navigate('/listener-clubs');
                return;
            }

            if (data?.signup_token) {
                // Unauthenticated — store token and show club preview
                sessionStorage.setItem("signup_token", data.signup_token);
                sessionStorage.setItem("signup_club", JSON.stringify(data.podcast_club));
                setClubPreview(data.podcast_club);
                return;
            }

            if (data?.podcast_club) {
                // Logged-in listener — already subscribed, navigate to club
                navigate(`/listener-podcast-club/${data.podcast_club.guid}`);
                return;
            }
        } catch (err) {
            posthog.captureException(err, { tags: { context: 'invite_redeem' } });
            setError(err.response?.data?.message || "Something went wrong. Please check the code and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="d-flex justify-content-center align-items-center py-5">
            <Card style={{ width: "100%", maxWidth: "480px" }}>
                <Card.Body className="p-4">
                    <div className="text-center mb-4">
                        <AnimatedImage
                            src="/img/podsy-logo-w-200.png"
                            alt="Podsy Logo"
                            loopMs={3000}
                            aberration={true}
                        />
                    </div>

                    {clubPreview ? (
                        // Step 2 — show club info and prompt to register/login
                        <>
                            <Alert variant="success">
                                <strong>You're joining:</strong>
                                <div className="mt-2">
                                    <div className="fw-bold">{clubPreview.title}</div>
                                    {clubPreview.description && (
                                        <div className="small text-muted mt-1">{clubPreview.description}</div>
                                    )}
                                </div>
                            </Alert>
                            <p className="text-muted small mb-3">
                                Create an account to complete your registration and get access to the club.
                            </p>
                            <div className="d-grid gap-2">
                                <Button variant="primary" onClick={() => navigate("/signup")}>
                                    Create account
                                </Button>
                                <Button variant="outline-secondary" onClick={() => navigate("/login")}>
                                    I already have an account
                                </Button>
                            </div>
                            <div className="text-center mt-3">
                                <Button variant="link" size="sm" onClick={() => { setClubPreview(null); sessionStorage.removeItem("signup_token"); sessionStorage.removeItem("signup_club"); }}>
                                    Use a different code
                                </Button>
                            </div>
                        </>
                    ) : (
                        // Step 1 — code entry
                        <>
                            <h5 className="mb-1">Join a club</h5>
                            <p className="text-muted small mb-4">Enter the invite code you received to get access.</p>

                            {error && <Alert variant="danger">{error}</Alert>}

                            <Form onSubmit={handleRedeem}>
                                <Form.Group className="mb-3" controlId="joinCode">
                                    <Form.Label>Invite code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. LEADER2026"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        disabled={loading}
                                        autoFocus
                                    />
                                </Form.Group>
                                <div className="d-grid">
                                    <Button type="submit" variant="primary" disabled={!code.trim() || loading}>
                                        {loading ? <><Spinner animation="border" size="sm" className="me-2" />Checking…</> : "Join"}
                                    </Button>
                                </div>
                            </Form>

                            <div className="text-center mt-4 small text-muted">
                                Already have an account? <Link to="/login">Log in</Link>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default Join;
